import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { ChangeEvent, FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'src/app/hooks';
import { getFileUrl, removeFile } from 'src/features/globalSlice';
import { GlobalFile } from 'src/store/models/Action';
import { Document } from 'src/store/models/Product';
import { useFileSelector } from 'src/utils/useFileSelector';

import Button from '../Button/Button';
import Tooltip from '../Tooltip/Tooltip';
import classes from './UploadFile.module.scss';
import UploadFileSkeleton from './UploadFileSkeleton';

export interface UploadFileProps {
  uploadTooltip?: string;
  className?: string;
  accept: string;
  multiple?: boolean;
  files?: Document[];
  setFiles?: (files: Document[]) => void;
  loading?: boolean;
  fileId?: string;
}

const UploadFile: FC<UploadFileProps> = ({
  uploadTooltip,
  className,
  accept,
  multiple = true,
  files: initialFiles,
  setFiles,
  loading = false,
  fileId,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();

  const files = useFileSelector({ id: fileId });

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    Array.from(e.target?.files)?.forEach((file) => {
      dispatch(getFileUrl({ file: file, id: fileId }));
    });
  };

  const removeFileHandler = (file: GlobalFile | Document) => {
    setFiles(initialFiles?.filter((f) => file.url !== f.url));
    dispatch(
      removeFile({ id: fileId, fileName: file.fileName, url: file.url })
    );
  };

  const fileList = [...initialFiles, ...files]?.map((d, i) => (
    <div className={classes.File} key={i}>
      <a rel="noopener noreferrer" target="_blank" href={d.url}>
        {d.fileName}
      </a>
      <Tooltip tooltipText={t('common:remove')}>
        <FontAwesomeIcon icon={faTrash} onClick={() => removeFileHandler(d)} />
      </Tooltip>
    </div>
  ));

  return (
    <div
      className={classNames(classes.Container, className, {
        [classes.Loading]: loading,
      })}
    >
      {!loading ? (
        <>
          <input
            onChange={onFileChange}
            type="file"
            name="file"
            multiple={multiple}
            accept={accept}
            ref={inputRef}
          />
          <Tooltip tooltipText={uploadTooltip || ''} disabled={!uploadTooltip}>
            <Button
              variant="outlined"
              onClick={() => inputRef.current?.click()}
            >
              {t('common:upload')}
            </Button>
          </Tooltip>
          {fileList?.length ? <section>{fileList}</section> : null}
        </>
      ) : (
        <UploadFileSkeleton />
      )}
    </div>
  );
};

export default UploadFile;
